import React, { useState, useRef, useEffect } from "react";
import styles from "./DashboardPage.module.css";
import { Vehicle } from "./types";
import { mockVehicles } from "./mockData";

export default function DashboardPage() {
  const [vehicles] = useState<Vehicle[]>(mockVehicles);
  const [selectedTab, setSelectedTab] = useState<"vehicles" | "rides">("vehicles");
  const [dividerPosition, setDividerPosition] = useState(60); // 60% map, 40% list
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate stats
  const stats = {
    online: vehicles.filter(v => v.status === "Online").length,
    activating: vehicles.filter(v => v.status === "Activating").length,
    active: vehicles.filter(v => v.status === "Active").length,
    inRide: vehicles.filter(v => v.status === "In-ride").length,
    issues: vehicles.filter(v => v.status === "Issues").length,
    total: vehicles.length
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newPosition = ((e.clientX - containerRect.left) / containerRect.width) * 100;
    
    // Limit between 20% and 80%
    if (newPosition >= 20 && newPosition <= 80) {
      setDividerPosition(newPosition);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className={styles.page} ref={containerRef}>
      {/* Left Side - Map */}
      <div className={styles.mapSection} style={{ width: `${dividerPosition}%` }}>
        {/* Map Controls */}
        <div className={styles.mapControls}>
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="Search Plate, Model, Driver..."
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filterButtons}>
            <button className={styles.filterBtn}>Off</button>
            <button className={styles.filterBtn}>Demand</button>
            <button className={`${styles.filterBtn} ${styles.filterBtnActive}`}>Layers</button>
            <button className={styles.filterBtn}>Waypoints</button>
          </div>
        </div>

        {/* Map Container */}
        <div className={styles.mapContainer}>
          <div className={styles.mapPlaceholder}>
            {/* Google Maps would go here */}
            <div className={styles.mapMessage}>
              <svg className={styles.mapIcon} width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              {/* <p>Map Integration Placeholder</p>
              <small>Google Maps API would be integrated here</small> */}
            </div>
          </div>

          {/* Map Legend */}
          <div className={styles.mapLegend}>
            <button className={styles.zoomBtn}>−</button>
            <button className={styles.zoomBtn}>+</button>
          </div>

          {/* Stats Footer */}
          <div className={styles.statsFooter}>
            <div className={styles.statItem}>
              <span className={styles.statDot} style={{ background: "#6b7280" }}></span>
              <span className={styles.statText}>{stats.online} Online</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statDot} style={{ background: "#f59e0b" }}></span>
              <span className={styles.statText}>{stats.activating} Activating</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statDot} style={{ background: "#3b82f6" }}></span>
              <span className={styles.statText}>{stats.active} Active</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statDot} style={{ background: "#10b981" }}></span>
              <span className={styles.statText}>{stats.inRide} In-ride</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statDot} style={{ background: "#ef4444" }}></span>
              <span className={styles.statText}>{stats.issues} Issues</span>
            </div>
            <div className={styles.statTime}>19:59 EST</div>
          </div>
        </div>
      </div>

      {/* Draggable Divider */}
      <div
        className={styles.divider}
        onMouseDown={handleMouseDown}
        style={{ left: `${dividerPosition}%` }}
      >
        <div className={styles.dividerHandle}></div>
      </div>

      {/* Right Side - Vehicle List */}
      <div className={styles.listSection} style={{ width: `${100 - dividerPosition}%` }}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${selectedTab === "vehicles" ? styles.tabActive : ""}`}
            onClick={() => setSelectedTab("vehicles")}
          >
            VEHICLES <span className={styles.tabCount}>{stats.total}</span>
          </button>
          <button
            className={`${styles.tab} ${selectedTab === "rides" ? styles.tabActive : ""}`}
            onClick={() => setSelectedTab("rides")}
          >
            RIDES <span className={styles.tabCount}>146</span>
          </button>
        </div>

        {/* Table Controls */}
        <div className={styles.tableControls}>
          <button className={styles.columnsBtn}>Columns</button>
          <button className={styles.resetBtn}>Reset</button>
        </div>

        {/* Vehicle Table */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>I...</th>
                <th>Plate</th>
                <th>Cl...</th>
                <th>M...</th>
                <th>Driver</th>
                <th>S</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {vehicles.slice(0, 100).map((vehicle) => (
                <tr key={vehicle.id}>
                  <td>
                    <div className={styles.vehicleIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                      </svg>
                    </div>
                  </td>
                  <td className={styles.plateCell}>{vehicle.plate}</td>
                  <td>{vehicle.class}</td>
                  <td>{vehicle.model}</td>
                  <td className={styles.driverCell}>{vehicle.driver}</td>
                  <td>
                    <div className={styles.actionIcons}>
                      <button className={styles.iconBtn}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M12 6v6l4 2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td>
                    <button className={styles.menuBtn}>...</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className={styles.tableFooter}>
          <span className={styles.footerText}>Showing 100 vehicles out of {stats.total}</span>
        </div>
      </div>
    </div>
  );
}